import { css } from 'lit';
import { get } from 'lodash';
import { DotNotationPath } from '@typings/shared-types';
import { DraftEvent, LocationTypes, RoadEventDto } from '../../typings/api';
import { APIErrorJSON } from '../../typings/shared-types';
import { isLaneImpacted, isValidNumber, isValidString } from './utils';

export const isLatLngExpression = (value: unknown): value is L.LatLngExpression => {
	if (!value || (typeof value !== 'object' && !Array.isArray(value))) return false;

	if (Array.isArray(value)) {
		return (value.length === 2 || value.length === 3) && value.every((v) => isValidNumber(v));
	}

	if (typeof value === 'object') {
		const obj = value as Record<string, unknown>;

		if (isValidNumber(obj.lat) && isValidNumber(obj.lng)) {
			return true;
		}

		if (isValidNumber(obj.lat) && isValidNumber(obj.lon)) {
			return true;
		}
	}

	return false;
};

export const isLatLngBoundsExpression = (value: unknown): value is L.LatLngBoundsExpression => {
	if (!value) return false;

	if (Array.isArray(value)) {
		return value.every(isLatLngExpression);
	}

	if (typeof value === 'object') {
		const obj = value as Record<string, unknown>;
		return typeof obj.getSouthWest === 'function' && typeof obj.getNorthEast === 'function';
	}

	return false;
};

export const isREMEvent = (target: unknown): target is RoadEventDto => {
	const remEvent = target as RoadEventDto;
	if (
		!!remEvent?.id &&
		!!remEvent?.eventType &&
		!!remEvent?.priorityLevel &&
		!!remEvent?.dateStart
	) {
		return true;
	}
	return false;
};

export const isREMDraftEvent = (
	target: unknown,
	requiredFields: Array<DotNotationPath<RoadEventDto>>,
): target is DraftEvent => {
	if (target == null) {
		return false;
	}

	const remEvent = target as DraftEvent;

	// special checks for lane impacts on major roads, field must not be null AND have at least one impacted lane/shoulder
	if (
		(requiredFields.includes('positiveLaneBlockage') ||
			requiredFields.includes('negativeLaneBlockage')) &&
		remEvent.eventLocation === LocationTypes.MAJOR_ROAD &&
		!isLaneImpacted(remEvent.positiveLaneBlockage) &&
		!isLaneImpacted(remEvent.negativeLaneBlockage)
	) {
		return false;
	}

	// check configured required fields are present
	if (!requiredFields.every((field) => get(remEvent, field) != null)) {
		return false;
	}

	if (
		remEvent.eventLocation === LocationTypes.LOCAL_ROAD &&
		remEvent.locationDetails?.crossStreetStart === undefined
	) {
		return false;
	}

	if ((remEvent?.respondingUnits ?? []).every((unit) => isValidString(unit.unitType))) {
		return true;
	}

	return false;
};

export const isAPIErrorJSON = (target: unknown): target is APIErrorJSON => {
	const apiErrorJson = target as APIErrorJSON;
	if (
		isValidString(apiErrorJson.error) &&
		isValidString(apiErrorJson.message) &&
		isValidString(apiErrorJson.path) &&
		isValidString(apiErrorJson.timestamp) &&
		isValidNumber(apiErrorJson.status)
	) {
		return true;
	}
	return false;
};
